'use client';
import { AddToCartButton } from '@eliadmin/core/src/components/product/add-to-cart-button';
import { ProductStatusText } from '@eliadmin/core/src/components/product/product-status-text';
import { fetchSlugDetail } from '@eliadmin/core/src/api/slugs/slugs-api';
import { AddToFavoritesButton } from '@eliadmin/core/src/components/product/add-to-favorites-button';
import { NotInStockDialog } from '@eliadmin/core/src/components/product/dialog/not-in-stock-dialog';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { useDialogStore } from '@eliadmin/core/src/components/ui/dialog';
import { Input } from '@eliadmin/core/src/components/ui/input';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { useGlobalStore } from '@eliadmin/core/src/stores/global-store';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { useVariantsStore } from '@eliadmin/core/src/stores/variant-store';
import {
  hasDimensions,
  isSellable,
  getProductMinQuantity,
  getProductQuantityRules,
  getProductQuantityStep,
  normalizeProductQuantity,
  normalizeProductQuantityDown,
} from '@eliadmin/core/src/utils/product-utils';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo } from 'react';
import { LuMinus, LuPlus } from 'react-icons/lu';
import { ProductInquiryButton } from './product-inquiry-button';
import { isHighValueProductPrice } from '../../../utils/high-value-product';
import { formatPrice } from '@eliadmin/core/src/utils/price-utils';
import { getProductPriceDisplay } from '@eliadmin/core/src/utils/product-price-utils';

export function ProductQuantity() {
  const product = useVariantsStore(s => s.product);
  const { me, isInitialized } = useCustomerStore();
  const authToken = me?.authToken;
  const locale = useLocaleStore(s => s.currentLocale);
  const settings = useGlobalStore(state => state.templateSettings);
  const selectedVariant = useVariantsStore(state => state.selectedVariant);
  const customValuesError = useVariantsStore(state => state.customValuesError);

  const selectedQuantity = useVariantsStore(state => state.selectedQuantity);
  const { t } = useTranslations();

  const setSelectedQuantity = useVariantsStore(state => state.setSelectedQuantity);
  const openDialog = useDialogStore(s => s.open);

  //   const selectedServices = useProductStore(state => state.selectedServices);
  const variants = useVariantsStore(state => state.product.variants);
  //   const open = useModalStore(state => state.open);
  //   const close = useModalStore(state => state.close);
  //   const api = useGlobalStore(state => state.config.api);
  //   const decimalPlaces = countDecimals(product.custom_quantity);

  const sellable = isSellable(selectedVariant ? selectedVariant : product, selectedQuantity);
  const quantityRules = useMemo(
    () => getProductQuantityRules(product, selectedVariant),
    [product, selectedVariant],
  );
  const minQuantity = useMemo(() => getProductMinQuantity(quantityRules), [quantityRules]);
  const step = useMemo(() => getProductQuantityStep(quantityRules), [quantityRules]);

  const hasSpecialProductStatus = product.status === 'ended' || product.status === 'arrival';

  const disabled =
    (product.quantity < 1 && !sellable) ||
    (selectedVariant && selectedVariant?.quantity < 1 && !sellable) ||
    ((variants?.length || 0) > 0 && !selectedVariant);

  const sortedSuppliers = [...product.suppliers].sort((a, b) => a.position - b.position);

  // main affiliate supplier is only when there is only one supplier
  const firstSupplier = sortedSuppliers[0];

  const handleChangeQuantity = (value: number) => {
    if (!value) {
      value = 1;
    }
    const newValue = normalizeProductQuantity(selectedQuantity + value, quantityRules);
    if (newValue <= minQuantity) {
      setSelectedQuantity(minQuantity);
    } else {
      const stock = selectedVariant ?? product;
      const sellableNext = isSellable(stock, newValue);
      if (!sellableNext && stock.quantity > 0) {
        openDialog({
          id: NotInStockDialog.id,
          content: <NotInStockDialog maxQuantity={stock.quantity} unit={product.unit ?? ''} />,
        });
        setSelectedQuantity(normalizeProductQuantityDown(stock.quantity, quantityRules));
      } else if (sellableNext) {
        setSelectedQuantity(newValue);
      }
    }
  };

  useEffect(() => {
    const tmp = selectedVariant ? selectedVariant : product;
    if (tmp.quantity <= 0 && tmp.availability?.sellable) {
      setSelectedQuantity(minQuantity);
    }
  }, [product, selectedVariant, setSelectedQuantity, minQuantity]);

  const currency = useGlobalStore(s => s.generalLayout.currency);
  const currencySettings = useGlobalStore(s => s.generalLayout.currency_settings);

  const personalizedProductQuery = useQuery({
    queryKey: ['PRODUCT_DETAIL_PERSONALIZED', product.slug, locale, me?.customer.id ?? 'guest'],
    queryFn: () =>
      fetchSlugDetail({ locale, slug: product.slug, authToken })
        .then(r => r['App\\Models\\Product'])
        .catch(() => null),
    enabled: isInitialized && !!product.slug,
  });

  const personalizedProduct = personalizedProductQuery.data ?? null;
  const personalizedSelectedVariant =
    selectedVariant && personalizedProduct?.variants
      ? personalizedProduct.variants.find(variant => variant.id === selectedVariant.id)
      : undefined;
  const displayProduct = personalizedProduct ?? product;
  const determinedObject = personalizedSelectedVariant ?? selectedVariant ?? displayProduct;
  const priceDisplay = getProductPriceDisplay(determinedObject, {
    isWholesaler: me?.customer.is_wholesaler,
    priceWithoutVatFirst: settings.product_detail?.price_without_vat_first?.enabled,
  });
  const highValueProduct =
    !priceDisplay.priceOnRequest && isHighValueProductPrice(priceDisplay.currentWithVat, currency);

  if (hasSpecialProductStatus) {
    return <ProductStatusText status={product.status} />;
  }

  return (
    <div className="flex w-full flex-col items-start justify-start gap-4 sm:gap-5 lg:gap-6">
      <div className="flex w-full flex-col items-start justify-start gap-2">
        {priceDisplay.priceOnRequest ? (
          <strong className="text-2xl font-medium tracking-tight sm:text-3xl">
            {t('text2278')}
          </strong>
        ) : (
          <>
            {priceDisplay.showMocVocPrices && priceDisplay.mocPrice != null ? (
              <p className="text-sm font-light text-black/70 sm:text-base">
                {t('text1926')} {formatPrice(priceDisplay.mocPrice, currency, currencySettings)}
              </p>
            ) : null}
            <div className="flex items-baseline justify-start gap-2">
              <strong className="text-3xl font-medium tracking-tight sm:text-4xl">
                {formatPrice(priceDisplay.current, currency, currencySettings)}
              </strong>
              {priceDisplay.showPreviousPrice ? (
                <p className="text-lg font-light text-black/70 line-through sm:text-xl lg:text-2xl">
                  {formatPrice(priceDisplay.beforeDiscount, currency, currencySettings)}
                </p>
              ) : null}
            </div>
            {settings.product_detail?.show_price_without_vat?.enabled ? (
              <p className="text-sm font-light text-black/70 sm:text-base">
                {priceDisplay.showMocVocPrices ? `${t('text1925')} ` : ''}
                {formatPrice(priceDisplay.secondary, currency, currencySettings)}{' '}
                {priceDisplay.secondaryIncludesVat ? t('text1122') : t('text443')}
              </p>
            ) : null}
          </>
        )}
      </div>
      {determinedObject?.availability?.id === 1 &&
        isSellable(determinedObject, selectedQuantity) && (
          <div className="flex h-[37px] items-center justify-center gap-2 rounded-full bg-[#00A000] bg-opacity-10 px-4 sm:h-10">
            <div className="h-[10px] w-[10px] rounded-full bg-[#00A000]" />
            <p className="text-sm font-medium text-[#00A000] sm:text-base">
              {determinedObject?.availability.name}
            </p>
          </div>
        )}

      {(highValueProduct || priceDisplay.priceOnRequest) && <ProductInquiryButton />}

      {!priceDisplay.priceOnRequest && (
        <div
          className={cn(
            'flex w-full flex-col gap-2 sm:flex-row md:flex-wrap md:gap-4 lg:flex-nowrap lg:gap-6',
            'ea-product-detail-quantity',
          )}
        >
          {!firstSupplier?.is_affiliate && (
            <div className="flex items-center justify-between gap-2">
              <Button
                variant="alternative-default"
                className={cn(
                  'h-[50px] w-full min-w-[50px] max-w-[50px] p-0',
                  'ea-product-detail-quantity__substract-button ea-product-detail-quantity__button',
                )}
                disabled={selectedQuantity <= minQuantity}
                aria-label={`${t('text22')} −`}
                onClick={() => {
                  handleChangeQuantity(-step);
                }}
              >
                <LuMinus size={16} strokeWidth={3} color="black" />
              </Button>
              <div className="max-h-[50px] min-h-[50px] w-full min-w-[50px] flex-grow lg:max-w-[50px] lg:flex-grow-0">
                <Input
                  rounded={'none'}
                  className={{
                    wrapper: cn('ea-product-quantity__input-wrapper'),
                    main: cn('ea-product-quantity__input', ''),
                  }}
                  value={selectedQuantity}
                  aria-label={t('text22')}
                  onChange={e => {
                    const value = parseInt(e.target.value);
                    if (value) {
                      handleChangeQuantity(value - selectedQuantity);
                    }
                  }}
                  onFocus={e => {
                    e.target.select();
                  }}
                />
              </div>
              <Button
                variant="alternative-default"
                className={cn(
                  'h-[50px] w-full min-w-[50px] max-w-[50px] p-0',
                  'ea-product-detail-quantity__increase-button ea-product-detail-quantity__button',
                )}
                // disabled={selectedQuantity >= product.quantity}
                aria-label={`${t('text22')} +`}
                onClick={() => {
                  handleChangeQuantity(step);
                }}
              >
                <LuPlus size={16} strokeWidth={3} color="black" />
              </Button>
            </div>
          )}
          <div
            className={cn('flex w-full gap-6', 'ea-product-detail-quantity__add-to-cart-container')}
          >
            {firstSupplier?.is_affiliate ? (
              <Button
                asChild
                className={cn(
                  'h-12 w-full flex-grow',
                  'ea-product-detail-quantity__go-to-supplier-button',
                )}
              >
                <a href={firstSupplier.url ?? '#'}>{t('text721')}</a>
              </Button>
            ) : (
              <AddToCartButton
                className={cn('w-full flex-grow', 'ea-product-detail-quantity__add-to-cart-button')}
                quantity={selectedQuantity}
                disabled={
                  disabled || (hasDimensions(product.attributes) ? customValuesError : false)
                }
                product={displayProduct}
                sellable={sellable}
                showIcon={true}
              />
            )}
            {settings.product_detail?.add_to_favorite?.enabled && (
              <AddToFavoritesButton
                slug={displayProduct.slug}
                isFavorite={displayProduct.is_favorite ?? false}
                productId={displayProduct.id}
                className={{
                  button: cn('max-h-[50px] min-h-[50px] min-w-[50px] max-w-[50px]'),
                }}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
}
